
import { Router } from "@angular/router";
import { inject, Inject, Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, map, Observable, of, switchMap, tap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { JwtHelperService } from "@auth0/angular-jwt";
import { SESSION_STORAGE, StorageService } from "ngx-webstorage-service";
import { CustomSnackBarService } from "./custom-snack-bar.service";
import { ParametrosGenericosService } from "./generics/parametros-genericos.service";
import { RelacaoGenericaService } from "./generics/relacao-generica.service";
import { ConfigService } from './config.service';
import { AuthData } from "../models/auth/auth-data";
import { AuthRequest } from "../models/auth/auth-request";
import { GenericIdbaRequestModel } from "../models/generic-idba-request-model";
import { MenuItemButton } from "../models/menu-item-button";
import { AESService } from "./aes.service";
import { AuthRequestGp } from "../models/auth/auth-request-gp";

@Injectable({
  providedIn: "root",
})

export class AuthService {
  private loggedIn = new BehaviorSubject<boolean>(false);

  private readonly jwtTokenName = "authERP";
  private readonly jwtTokenMenu = "menuERP";
  private token: string | null = null;

  loggedUserName: string;
  loggedUserId: number;
  loggedUserType: Number;
  loggedUserEmail: string;
  loggedEmpresaUser: number;
  loggedNomeEmpresaUser: string;
  rowidLoggedUser: string;
  id: number;

  authData: AuthData;

  active: Boolean;
  loggedTime: Date;
  tokenApp: string | undefined;
  tokenBackEnd: string | undefined;

  public configService = inject(ConfigService);

  constructor(
    private router: Router,
    private http: HttpClient,
    private snackBar: CustomSnackBarService,
    private aesService: AESService,
    private parametroGenericoService: ParametrosGenericosService,
    private relacaoGenericaService: RelacaoGenericaService,
    @Inject(SESSION_STORAGE) private storage: StorageService,

  ) { }

  initialize(): void {

    if (this.configService.getApp() === "gp") {
      const tokenGp = localStorage.getItem('tokenGP');
      const usuarioGp = localStorage.getItem('usuarioGP');

      if (!tokenGp || !usuarioGp) {
        this.loggedIn.next(false);
        return;
      }

      try {
        const usuario = JSON.parse(usuarioGp);

        this.setToken(tokenGp);
        this.id = usuario.id;
        this.loggedUserName = usuario.nome ?? usuario.login;
        this.loggedIn.next(true);
      } catch {
        this.logoutGp();
      }
      return;
    }

    let token: string = localStorage.getItem(this.jwtTokenName) as string;
    if (token) {
      if (this.configService.isProd()) {
        try {
          token = this.aesService.decrypt(token);
        }
        catch {
          this.snackBar.openSnackBar("Sessão Inválida!", "error");
          this.logout("", true);
          return;
        }
      }
      else {
        if (!this.IsJsonString(token)) {
          this.snackBar.openSnackBar("Sessão Inválida!", "error");
          this.logout("", true);
          return;
        }
      }

      this.authData = new AuthData(JSON.parse(token));
      this.id = this.authData.id;
      this.loggedUserName = this.authData.nomeUsuario;
      this.loggedUserId = this.authData.codigoUsuario;
      this.loggedUserType = this.authData.indTipoUsuario;
      this.active = this.authData.indAtivo;
      this.loggedTime = this.authData.dataHoraLogin;
      this.loggedUserEmail = this.authData.emailUsuario;
      this.loggedEmpresaUser = this.authData.codigoEmpresa;
      this.loggedNomeEmpresaUser = this.authData.nomeEmpresa;
      this.tokenApp = this.authData.tokenApp;
      this.tokenBackEnd = this.authData.tokenBackEnd;
      this.rowidLoggedUser = this.authData.rowid;
      this.loggedIn.next(true);
    }
  }

  login(authRequest: AuthRequest): Observable<AuthData> {
    //authRequest.password = this.aesService.encrypt(authRequest.password);
    return this.http
      .post<any>(this.configService.getApiUrl('loginUrl'), authRequest, { observe: 'response' })
      .pipe(
        map(resp => {
          this.setToken(resp.body.token);

          localStorage.setItem('refreshToken', resp.body.refreshToken);

          const data = resp.body.data.usuario[0];
          const authData = new AuthData();

          authData.id = data.id;
          authData.codigoUsuario = data.codigoUsuario;
          authData.nomeUsuario = data.nome;
          authData.loginUsuario = data.loginUsuario;
          authData.emailUsuario = data.email;
          authData.tokenBackEnd = resp.body.token;
          authData.dataHoraLogin = new Date();
          authData.dataHoraValidade = new Date(authData.dataHoraLogin.getTime() + Number(ConfigService.settings.auth.sessionTimeoutMs));
          authData.userAgent = window.navigator.userAgent;
          authData.rowid = data.rowid;
          authData.isAdministrador = data?.isAdministrador;
          authData.isFuncionario = data?.isFuncionario;
          authData.isCoordenador = data?.isCoordenador;
          authData.isTerceiro = data?.isTerceiro;

          this.atualizaUsuarioLogado(authData);

          return authData;
        })
      );
  }

  refreshToken() {
    const request = {
      refreshToken: localStorage.getItem('refreshToken')
    }

    return this.http
      .post<any>(this.configService.getApiUrl('refreshUrl'), request, { observe: 'response', withCredentials: true })
      .pipe(
        map(resp => {

          let token: string = localStorage.getItem(this.jwtTokenName) as string;
          if (token) {
            if (this.configService.isProd()) {
              try {
                token = this.aesService.decrypt(token);
              }
              catch {
                this.snackBar.openSnackBar("Sessão Inválida!", "error");
                this.logout("", true);
              }
            }
            else {
              if (!this.IsJsonString(token)) {
                this.snackBar.openSnackBar("Sessão Inválida!", "error");
                this.logout("", true);
              }
            }

            this.authData = new AuthData(JSON.parse(token));
            this.authData.tokenBackEnd = resp.body.token;
            this.setTokenBackEnd(resp.body.token);
            this.setSession(this.authData);
          }

          this.setToken(resp.body.token);
          localStorage.setItem('refreshToken', resp.body.refreshToken);
          return resp;
        })
      );
  }

  setToken(token: string) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  atualizaUsuarioLogado(authData: AuthData) {
    this.setSession(authData);
    this.carregaParametros(authData, this);
  }

  atualizaParametros() {
    this.carregaParametros(this.authData, this);
  }

  carregaParametros(authData: AuthData, authService: AuthService) {

    forkJoin([this.parametroGenericoService.listAll(),
    this.relacaoGenericaService.listarRelacaoGenericaPorUsuario(),
    ]).subscribe(([parametroGenerico, relacaoGenerica]) => {

      authData["PR"] = {};

      authData["RG"] = {};

      for (let index = 0; index < relacaoGenerica?.body?.data?.registro?.length; index++) {
        const element = relacaoGenerica.body.data.registro[index];

        if (!authData["RG"][element.assunto])
          authData["RG"][element.assunto] = {};
        if (!authData["RG"][element.assunto][element.codigo])
          authData["RG"][element.assunto][element.codigo] = {};

        authData["RG"][element.assunto][element.codigo] = element.valor;
      }

      parametroGenerico?.body?.data?.forEach(element => {
        if (!authData["PR"][element.abrevModulo])
          authData["PR"][element.abrevModulo] = {};
        if (!authData["PR"][element.abrevModulo][element.assunto])
          authData["PR"][element.abrevModulo][element.assunto] = {};
        authData["PR"][element.abrevModulo][element.assunto] = element.valor
      });

      this.loggedIn.next(true);

      authService.setSession(authData);
    })
  }

  getLoggedUserName() {
    return this.loggedUserName;
  }

  getLoggedUserId() {
    return this.loggedUserId;
  }

  getLoggedUserType() {
    return this.loggedUserType;
  }

  get email() {
    return this.loggedUserEmail;
  }

  getLoggedEmpresaUser() {
    return this.loggedEmpresaUser;
  }

  getLoggedEmpresaNomeUser() {
    return this.getLoggedEmpresaNomeUser;
  }

  getTokenApp() {
    return this.tokenApp;
  }

  getTokenBackEnd() {
    return this.tokenBackEnd;
  }

  isActive() {
    return this.active;
  }

  getLoggedTime() {
    return this.loggedTime
  }

  getIsLoggedIn() {
    return this.loggedIn.getValue();
  }

  getLoggedUserRowid() {
    return this.rowidLoggedUser;
  }

  getUserId() {
    return this.id;
  }

  getModuloContratoHabilitado(): boolean {
    const parametro = this.getParametroGenerico("BAS", "ModuloContratoHabilitado");
    if (parametro === undefined)
      return false;
    else
      return JSON.parse(this.getParametroGenerico("BAS", "ModuloContratoHabilitado"));
  }

  getParametroGenerico(modulo: string, assunto: string) {
    if (this.authData && this.authData["PR"]) {
      if (this.authData["PR"][modulo]) {
        if (this.authData["PR"][modulo][assunto]) {
          return this.authData["PR"][modulo][assunto]
        }
      }
    }
    return undefined;
  }

  getParametroRelacao(assunto: string, nomeParametro: string) {
    if (this.authData && this.authData["RG"]) {
      if (this.authData["RG"][assunto]) {
        if (this.authData["RG"][assunto][nomeParametro]) {
          return this.authData["RG"][assunto][nomeParametro];
        }
      }
    }
    return undefined;
  }

  setTokenBackEnd(value: string) {
    this.tokenBackEnd = value;
  }

  public get isLoggedIn() {
    this.loggedIn.next(this.getSession());
    return this.loggedIn.asObservable();
  }

  invalidateSession() {
    localStorage.removeItem(this.jwtTokenName);
    this.tokenApp = undefined;
    this.tokenBackEnd = undefined;
    this.loggedIn.next(false);
  }

  logout(urlRedirect: string = "", indVerificaUrlAtual: boolean = false) {
    this.http
      .post<any>(this.configService.getApiUrl('logoutUrl'), {}, { observe: 'response' })
      .subscribe({ error: () => undefined });

    localStorage.removeItem(this.jwtTokenName);
    localStorage.removeItem("menu_erp");
    localStorage.removeItem("menu_gp");
    localStorage.removeItem(this.jwtTokenMenu);
    localStorage.removeItem('refreshToken');

    this.storage.clear();
    this.tokenApp = undefined;
    this.tokenBackEnd = undefined;
    this.loggedIn.next(false);

    if (indVerificaUrlAtual)
      urlRedirect = this.router.url;

    urlRedirect = urlRedirect.substring(1).trim();

    if (urlRedirect && !urlRedirect.includes("sign-in")) {
      var urlAux = this.aesService.encrypt(urlRedirect);
      urlAux = encodeURIComponent(urlAux);
      this.router.navigateByUrl(`/public/sign-in?p=${urlAux}`);

    }
    else
      this.router.navigate(["/public/sign-in"]);

  }

  validaSessao(authRequest: AuthRequest, authData: AuthData): boolean | undefined {

    const helper = new JwtHelperService();
    var token = helper.decodeToken(authData.tokenBackEnd ?? "");

    if (authRequest.user != authData.loginUsuario || authRequest.user != token.userId) {
      this.snackBar.openSnackBar("Acesso Inválido", null, "Error");
      this.logout("", true);
      return undefined;
    }

    return true;
  }

  isTokenExpired(token?: string): boolean | undefined {
    if (!token) token = localStorage.getItem(this.jwtTokenName) as string;
    if (!token) return true;

    if (this.configService.isProd()) {
      try {
        token = this.aesService.decrypt(token);
      }
      catch (error) {
        return true;
      }
    }
    else if (!this.IsJsonString(token)) {
      this.snackBar.openSnackBar("Sessão Inválida!", "error");
      this.logout("", true);
      return undefined;
    }

    this.authData = JSON.parse(token as string);
    var data = new Date();
    var dataValidade: Date | undefined = undefined;
    if (this.authData.dataHoraValidade != null)
      dataValidade = new Date(this.authData.dataHoraValidade);

    return (dataValidade === undefined || data.getTime() > dataValidade.getTime()); //Verificar Aqui

  }

  private setSession(authData: AuthData) {
    var token: string = JSON.stringify(authData);
    if (this.configService.isProd()) {
      token = this.aesService.encrypt(token);
    }
    localStorage.setItem(this.jwtTokenName, token);
    this.loggedUserName = authData.nomeUsuario;
    this.loggedUserId = authData.codigoUsuario;
    this.loggedUserType = authData.indTipoUsuario;
    this.loggedTime = authData.dataHoraLogin;
    this.tokenApp = authData.tokenApp;
    this.tokenBackEnd = authData.tokenBackEnd;
    this.loggedUserEmail = authData.emailUsuario;
    this.rowidLoggedUser = authData.rowid;
    this.id = authData.id;
    this.loggedIn.next(true);
  }

  private getSession(): boolean {

    if (this.configService.getApp() === "gp") {
      return !!localStorage.getItem('tokenGP');
    }

    var token: string = localStorage.getItem(this.jwtTokenName) as string;
    if (!token) return false;
    if (this.configService.isProd()) {
      try {
        token = this.aesService.decrypt(token);
      }
      catch (error) {
        return false;
      }
    }
    else if (!this.IsJsonString(token)) {
      this.snackBar.openSnackBar("Sessão Inválida!", "error");
      this.logout("", true);
      return false;
    }

    if (!token) {
      return false;
    }
    this.authData = JSON.parse(token);
    if (this.authData) {
      return true;
    }
    else {
      return false;
    }
  }

  renewToken() {
    var token: string = localStorage.getItem(this.jwtTokenName) as string;

    if (this.configService.isProd()) {
      try {
        token = this.aesService.decrypt(token);
      }
      catch (error) {
        this.snackBar.openSnackBar("Sessão Inválida!", "error");
        this.logout("", true);
        return;
      }
    }
    else if (!this.IsJsonString(token)) {
      this.snackBar.openSnackBar("Sessão Inválida!", "error");
      this.logout("", true);
      return;
    }

    var authData = new AuthData(JSON.parse(token));
    authData.dataHoraValidade = new Date(new Date().getTime() + ConfigService.settings.auth.sessionTimeoutMs);
    if (this.tokenBackEnd) {
      authData.tokenBackEnd = this.tokenBackEnd;
    }
    this.setSession(authData);
  }

  buscaEstruturaMenu(codigoAplicacao: string = "ERP") {
    var request: GenericIdbaRequestModel = new GenericIdbaRequestModel();
    request.program = "bas/acesso/menu.p";
    request.procedure = "pi-monta-estrutura-menu";
    return this.http.post<any>(this.configService.getApiUrl('integrateUrl'), request, { observe: "response" });
  }


  setEstruturaMenu(estruturaMenu: MenuItemButton[]) {
    var token: string = JSON.stringify(estruturaMenu);
    if (this.configService.isProd()) {
      token = this.aesService.encrypt(token);
    }
    localStorage.setItem(this.getMenuStorageKey(), token);
  }

  retornaEstruturaMenu(): MenuItemButton[] | undefined {
    const menuKey = this.getMenuStorageKey();
    var tokenMenu: string = localStorage.getItem(menuKey) as string;
    if (!tokenMenu && menuKey !== this.jwtTokenMenu) {
      tokenMenu = localStorage.getItem(this.jwtTokenMenu) as string;
    }

    if (tokenMenu) {
      if (this.configService.isProd()) {
        try {
          tokenMenu = this.aesService.decrypt(tokenMenu);
        }
        catch {
          return undefined;
        }
      }
      else {
        if (!this.IsJsonString(tokenMenu)) {
          return undefined;
        }
      }
      return JSON.parse(tokenMenu);
    }
    return undefined;
  }

  private getMenuStorageKey() {
    const appId = String(ConfigService.settings.app.id || 'erp').trim().toLowerCase();
    return `menu_${appId}`;
  }


  private IsJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  buscarParametroSessao(parametro: string): any | null {
    const dados = localStorage.getItem("parametros");
    if (!dados) return null;

    const lista = JSON.parse(dados);
    const item = lista.find((x: any) => x.parametro === parametro);
    return item ? item.valor : null;
  }

  //GP
  loginGP(authRequest: AuthRequestGp): Observable<boolean> {
    return this.http
      .post<any>(
        this.configService.getServiceUrl('gp') + "/usuario/login",
        authRequest,
        { observe: 'response' }
      )
      .pipe(
        switchMap(resp => {
          const data = resp.body?.data;
          if (resp.body?.success === false || !data?.accessToken) {
            const message = typeof resp.body?.message === 'string' && resp.body.message.trim()
              ? resp.body.message
              : 'Não foi possível entrar. Verifique seu usuário e senha.';
            this.snackBar.openSnackBar(message, null, 'Error');
            return of(false);
          }

          this.setToken(data.accessToken);
          this.id = data.id;
          this.loggedUserName = data.nome ?? data.login;

          localStorage.setItem('tokenGP', data.accessToken);
          localStorage.setItem('tokenTotvs', data.tokenTotvs);
          localStorage.setItem('usuarioGP', JSON.stringify(data));

          this.loggedIn.next(true);
          return this.carregaParametrosGP();
        }),
      );
  }

  private carregaParametrosGP(): Observable<boolean> {
    return this.http
      .get<any>(
        `${this.configService.getServiceUrl('gp')}/parametroGeral`,
        { observe: 'response' }
      )
      .pipe(
        tap(resp => {
          localStorage.setItem('parametros', JSON.stringify(resp.body.data));
        }),
        map(() => true)
      );
  }

  buscaEstruturaMenuGp() {
    const params = {
      Format: "Tree",
      Attributes: "Id,Descricao,Identificador,TipoMenu,RotaWeb,RotaApp,PermissaoItemId,ArquivoId,Caminho,PermissaoMenuId,Sequencia,Icone",
    }
    return this.http.get<any>(this.configService.getServiceUrl('gpd') + '/permissoes/menus', { observe: "response", params });
  }

  logoutGp() {
    localStorage.removeItem("menu_gp");
    localStorage.removeItem("tokenGP");
    localStorage.removeItem("tokenTotvs");
    localStorage.removeItem("usuarioGP");
    localStorage.removeItem("parametros");

    this.storage.clear();
    this.tokenApp = undefined;
    this.tokenBackEnd = undefined;
    this.token = null;
    this.id = undefined!;
    this.loggedUserName = undefined!;
    this.loggedIn.next(false);
    this.router.navigate(["/public/sign-in"]);
  }
}
